import { useQuery, useQueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

/** `onError` is optional for a subscriber to honour; one that does lets a
 *  failed listener (rules, a missing index) end the load instead of leaving the
 *  query pending for good. */
export type Subscribe<T> = (onData: (data: T) => void, onError: (error: Error) => void) => () => void;

/**
 * Binds a Firestore `onSnapshot` subscription to the TanStack Query cache
 * (ADR-0002). Components consume a plain `useQuery` result and cannot tell the
 * data is pushed. The subscription keeps the cache fresh (`staleTime: Infinity`);
 * it starts on mount and is torn down on unmount so listeners never leak.
 */
export function useLiveQuery<T>(queryKey: QueryKey, subscribe: Subscribe<T>): UseQueryResult<T> {
  const qc = useQueryClient();
  const keyStr = JSON.stringify(queryKey);

  useEffect(() => {
    // Errors are reported through the query function's own listener below.
    const unsub = subscribe(
      (data) => qc.setQueryData(queryKey, data),
      () => {},
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyStr]);

  return useQuery<T>({
    queryKey,
    queryFn: () =>
      new Promise<T>((resolve, reject) => {
        const existing = qc.getQueryData<T>(queryKey);
        if (existing !== undefined) {
          resolve(existing);
          return;
        }
        const unsub = subscribe(
          (data) => {
            unsub();
            resolve(data);
          },
          (error) => {
            unsub();
            reject(error);
          },
        );
      }),
    staleTime: Infinity,
  });
}
